import React, { useState } from 'react';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  Badge,
  Avatar,
  Menu,
  MenuItem,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Notifications as NotificationsIcon,
  AccountCircle,
  Settings as SettingsIcon,
  Logout as LogoutIcon,
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  SmartToy as BotIcon,
  School as KnowledgeIcon,
  Analytics as AnalyticsIcon,
  Settings as SettingsIcon2,
  Webhook as WebhookIcon,
  Business as BusinessIcon,
  Psychology as PsychologyIcon,
  Apartment as ApartmentIcon,
  VpnKey as VpnKeyIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useSocket } from '../../contexts/SocketContext';
import { useUserRole } from '../../contexts/UserRoleContext';
import NotificationPanel from '../Notifications/NotificationPanel';
import Sidebar from './Sidebar';

const drawerWidth = 280;

const Layout = ({ children }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [notificationOpen, setNotificationOpen] = useState(false);
  
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { notifications } = useSocket();
  const { userRole, isAdmin, isCustomer, userCompany } = useUserRole();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
    handleProfileMenuClose();
  };

  const handleSettings = () => {
    navigate('/settings');
    handleProfileMenuClose();
  };

  const unreadNotifications = notifications.filter(n => !n.read).length;

  // Menüpunkte für Administrator (Gründer)
  const adminMenuItems = [
    { 
      text: 'Dashboard', 
      icon: <DashboardIcon />, 
      path: '/dashboard', 
      requiresApproval: false,
      description: 'Übersicht über alle wichtigen Kennzahlen und Statistiken Ihres KiChatbot Systems'
    },
    { 
      text: 'Chat Sessions', 
      icon: <BotIcon />, 
      path: '/chat-sessions', 
      requiresApproval: true,
      description: 'Verwalten und überwachen Sie alle Chat-Unterhaltungen'
    },
    { 
      text: 'Mandanten', 
      icon: <ApartmentIcon />, 
      path: '/tenants', 
      requiresApproval: true,
      description: 'Shopify-Embed und Mandanten-Slug für das öffentliche Widget'
    },
    { 
      text: 'API-Credentials', 
      icon: <VpnKeyIcon />, 
      path: '/credentials', 
      requiresApproval: true,
      description: 'LLM- und Shopify-Storefront-Token pro Mandant (verschlüsselt)'
    },
    { 
      text: 'Firmen', 
      icon: <BusinessIcon />, 
      path: '/companies', 
      requiresApproval: true,
      description: 'Verwalten Sie alle Firmen und deren Konfigurationen'
    },
    { 
      text: 'Bots', 
      icon: <BotIcon />, 
      path: '/bots', 
      requiresApproval: true,
      description: 'Erstellen und verwalten Sie Chatbot-Instanzen für verschiedene Firmen'
    },
    { 
      text: 'Bot Konfiguration', 
      icon: <PsychologyIcon />, 
      path: '/bot-config', 
      requiresApproval: true,
      description: 'Konfigurieren Sie KI-Modelle, Prompts und Bot-Verhalten'
    },
    { 
      text: 'Widget Vorschau', 
      icon: <AnalyticsIcon />, 
      path: '/widget-preview', 
      requiresApproval: true,
      description: 'Testen Sie Ihr Chat-Widget und passen Sie das Design an'
    },
    { 
      text: 'Benutzer', 
      icon: <PeopleIcon />, 
      path: '/users', 
      requiresApproval: true,
      description: 'Verwalten Sie alle Benutzerkonten und deren Berechtigungen'
    },
    { 
      text: 'Analytics', 
      icon: <AnalyticsIcon />, 
      path: '/analytics', 
      requiresApproval: true,
      description: 'Detaillierte Analysen und Berichte über Bot-Performance und Nutzung'
    },
    { 
      text: 'Wissensdatenbank', 
      icon: <KnowledgeIcon />, 
      path: '/knowledge', 
      requiresApproval: true,
      description: 'Verwalten Sie die Wissensbasis und weisen Sie sie Bots zu'
    },
    { 
      text: 'Webhooks', 
      icon: <WebhookIcon />, 
      path: '/webhooks', 
      requiresApproval: true,
      description: 'Konfigurieren Sie Webhooks für die Integration mit externen Systemen'
    },
    { 
      text: 'Einstellungen', 
      icon: <SettingsIcon2 />, 
      path: '/settings', 
      requiresApproval: true,
      description: 'Allgemeine Systemeinstellungen und Konfigurationen'
    },
  ];

  // Menüpunkte für Kunden
  const customerMenuItems = [
    { 
      text: 'Dashboard', 
      icon: <DashboardIcon />, 
      path: '/dashboard', 
      requiresApproval: false,
      description: 'Übersicht über Ihre Bot-Performance und Statistiken'
    },
    { 
      text: 'Bots', 
      icon: <BotIcon />, 
      path: '/bots', 
      requiresApproval: true,
      description: 'Erstellen und verwalten Sie Ihre Chatbot-Instanzen'
    },
    { 
      text: 'Widget Vorschau', 
      icon: <AnalyticsIcon />, 
      path: '/widget-preview', 
      requiresApproval: true,
      description: 'Testen Sie Ihr Chat-Widget und passen Sie das Design an'
    },
    { 
      text: 'Mitglieder', 
      icon: <PeopleIcon />, 
      path: '/company-members', 
      requiresApproval: true,
      description: 'Verwalten Sie Benutzerrechte für Ihr Unternehmen'
    },
    { 
      text: 'Bot Konfiguration', 
      icon: <BotIcon />, 
      path: '/bot-config', 
      requiresApproval: true,
      description: 'Konfigurieren Sie Ihre ausgewählten Bots'
    },
    { 
      text: 'Wissensdatenbank', 
      icon: <KnowledgeIcon />, 
      path: '/knowledge', 
      requiresApproval: true,
      description: 'Verwalten Sie die Wissensbasis für Ihre Bots'
    },
    { 
      text: 'API-Credentials', 
      icon: <VpnKeyIcon />, 
      path: '/credentials', 
      requiresApproval: true,
      description: 'KI- und Shopify-Storefront-Token für Ihren Mandanten'
    },
  ];

  // Menüpunkte basierend auf Benutzerrolle auswählen
  const allMenuItems = isAdmin ? adminMenuItems : customerMenuItems;

  // Alle Menüpunkte für die Sidebar (inklusive gesperrte)
  const allMenuItemsForSidebar = allMenuItems;

  return (
    <Box sx={{ display: 'flex', width: '100vw', height: '100vh', overflow: 'hidden' }}>
      {/* App Bar */}
      <AppBar
        position="fixed"
        sx={{
          width: { md: `calc(100% - ${drawerWidth}px)` },
          ml: { md: `${drawerWidth}px` },
          backgroundColor: '#FFFFFF',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
          zIndex: 1200,
        }}
      >
        <Toolbar>
          <IconButton
            color="primary"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          
          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
            {/* Leerer Bereich - Logo und Admin Text entfernt */}
          </Box>

          {/* Notifications */}
          <IconButton
            color="primary"
            onClick={() => setNotificationOpen(true)}
            sx={{ mr: 2 }}
          >
            <Badge badgeContent={unreadNotifications} color="error">
              <NotificationsIcon />
            </Badge>
          </IconButton>

          {/* Profile Menu */}
          <IconButton
            size="large"
            edge="end"
            aria-label="account of current user"
            aria-controls="primary-search-account-menu"
            aria-haspopup="true"
            onClick={handleProfileMenuOpen}
            color="primary"
          >
            <Avatar
              sx={{
                width: 32,
                height: 32,
                backgroundColor: '#FFD700',
                color: '#000000',
                fontWeight: 600,
              }}
            >
              {user?.name?.charAt(0)?.toUpperCase() || 'A'}
            </Avatar>
          </IconButton>
          
          <Menu
            anchorEl={anchorEl}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            keepMounted
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            open={Boolean(anchorEl)}
            onClose={handleProfileMenuClose}
            PaperProps={{
              sx: {
                mt: 1.5,
                minWidth: 200,
                borderRadius: 2,
                boxShadow: '0 4px 16px rgba(0, 0, 0, 0.15)',
              },
            }}
          >
            <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid #e0e0e0' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                {user?.name || 'Admin'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {user?.email || 'admin@example.com'}
              </Typography>
            </Box>
            
            <MenuItem onClick={handleSettings}>
              <SettingsIcon sx={{ mr: 2 }} />
              Einstellungen
            </MenuItem>
            
            <Divider />
            
            <MenuItem onClick={handleLogout}>
              <LogoutIcon sx={{ mr: 2 }} />
              Abmelden
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      {/* Sidebar */}
      <Box
        component="nav"
        sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              backgroundColor: '#ffffff',
              borderRight: '1px solid #e0e0e0',
            },
          }}
        >
          <Sidebar menuItems={allMenuItemsForSidebar} onItemClick={() => setMobileOpen(false)} />
        </Drawer>
        
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              backgroundColor: '#ffffff',
              borderRight: '1px solid #e0e0e0',
              position: 'fixed',
              height: '100vh',
              zIndex: 1100,
            },
          }}
          open
        >
          <Sidebar menuItems={allMenuItemsForSidebar} />
        </Drawer>
      </Box>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { md: `calc(100vw - ${drawerWidth}px)` },
          mt: '64px',
          minHeight: 'calc(100vh - 64px)',
          backgroundColor: '#ffffff',
          color: '#000000',
          overflow: 'auto',
        }}
      >
        {children}
      </Box>

      {/* Notification Panel */}
      <NotificationPanel
        open={notificationOpen}
        onClose={() => setNotificationOpen(false)}
      />
    </Box>
  );
};

export default Layout;
